import { z } from 'zod';

/**
 * Every monetary value that crosses a process boundary in this platform is an
 * integer count of the currency's minor unit encoded as a canonical decimal
 * string. Safe integer numbers are accepted as a backwards-compatible input,
 * but output is always a string.
 */
const unsignedDecimal = z.string().regex(/^(?:0|[1-9]\d*)$/, 'must be canonical minor units');
const signedDecimal = z
  .string()
  .regex(/^(?:0|-?[1-9]\d*)$/, 'must be canonical signed minor units');
const safeInteger = z
  .number()
  .int('monetary amounts must be integer minor units')
  .safe('numeric monetary inputs must be safe integers');

export const MinorUnits = z.codec(
  z.union([unsignedDecimal, safeInteger.nonnegative()]),
  unsignedDecimal,
  {
    decode: (value) => String(value),
    encode: (value) => value,
  },
);

export const SignedMinorUnits = z.codec(z.union([signedDecimal, safeInteger]), signedDecimal, {
  decode: (value) => String(value),
  encode: (value) => value,
});

export type MinorUnits = z.infer<typeof MinorUnits>;
export type SignedMinorUnits = z.infer<typeof SignedMinorUnits>;

export const CurrencyCode = z
  .string()
  .min(2)
  .max(12)
  .regex(/^[A-Z0-9]+$/, 'currency must be an uppercase currency or token code');

export type CurrencyCode = z.infer<typeof CurrencyCode>;

/**
 * Minor unit exponents for the currencies we onboard.
 *
 * These are the operator's quote exponents, which is not the same list as
 * ISO 4217. A player's balance is crypto; a fiat currency is a price put on
 * it, and a price does not have to stop where the banknotes do. Yen and won
 * are carried at two decimals for exactly that reason: a ten-cent minimum
 * priced into whole yen is ¥2 — a 27% jump the player did not ask for —
 * whereas at two decimals it is ¥1.58, the number the exchange rate
 * actually gives. VND stays whole because the operator prices it in
 * thousands and has its own handling for that.
 *
 * casapi's `gspCurrencyExponent` has to agree, code for code, or an `amount`
 * of `158` means ¥1.58 on one side of the wire and ¥158 on the other. Never
 * change one without the other.
 */
export const DEFAULT_CURRENCY_EXPONENTS: Readonly<Record<string, number>> = {
  USD: 2,
  EUR: 2,
  GBP: 2,
  CNY: 2,
  HKD: 2,
  SGD: 2,
  MYR: 2,
  THB: 2,
  PHP: 2,
  AUD: 2,
  INR: 2,
  BRL: 2,
  IDR: 2,
  // The rest of what the operator's own currency catalogue offers a player.
  // A currency missing here is a launch refused for want of an exponent, and
  // the player picked it upstream long before we heard about it.
  CAD: 2,
  CHF: 2,
  NOK: 2,
  SEK: 2,
  NZD: 2,
  PLN: 2,
  MXN: 2,
  TRY: 2,
  ARS: 2,
  ZAR: 2,
  JPY: 2,
  KRW: 2,
  VND: 0,
  USDT: 6,
};

const currencyExponents = new Map(Object.entries(DEFAULT_CURRENCY_EXPONENTS));

export const SUPPORTED_CURRENCIES = Object.keys(DEFAULT_CURRENCY_EXPONENTS);

export function isSupportedCurrency(currency: string): boolean {
  return currencyExponents.has(currency);
}

export function setCurrencyExponent(currency: string, exponent: number): void {
  const code = CurrencyCode.parse(currency);
  if (!Number.isInteger(exponent) || exponent < 0 || exponent > 18) {
    throw new Error('currency exponent must be an integer between 0 and 18');
  }
  currencyExponents.set(code, exponent);
}

export function configureCurrencyExponents(exponents: Readonly<Record<string, number>>): void {
  for (const [currency, exponent] of Object.entries(exponents)) {
    setCurrencyExponent(currency, exponent);
  }
}

export function resetCurrencyExponents(): void {
  currencyExponents.clear();
  for (const [currency, exponent] of Object.entries(DEFAULT_CURRENCY_EXPONENTS)) {
    currencyExponents.set(currency, exponent);
  }
}

/** Convert a compatible in-process value without accepting an already-lossy number. */
export function minorUnitsToBigInt(value: string | number | bigint): bigint {
  if (typeof value === 'number' && !Number.isSafeInteger(value)) {
    throw new Error('numeric monetary inputs must be safe integers');
  }
  return BigInt(value);
}

export function currencyExponent(currency: string): number {
  const exponent = currencyExponents.get(currency);
  if (exponent === undefined) {
    throw new Error(`unsupported currency: ${currency}`);
  }
  return exponent;
}

/** Convert a human-readable major-unit decimal to canonical minor units. */
export function toMinorUnits(major: string | number, currency: string): MinorUnits {
  const exponent = currencyExponent(currency);
  const input = String(major);
  const match = /^(\d+)(?:\.(\d+))?$/.exec(input);
  if (!match) throw new Error('major amount must be a non-negative decimal');
  const fraction = match[2] ?? '';
  if (fraction.length > exponent) {
    throw new Error(`major amount has more than ${exponent} decimal places`);
  }
  return `${match[1]}${fraction.padEnd(exponent, '0')}`.replace(/^0+(?=\d)/, '');
}

/** Convert minor units to an exact major-unit decimal string. */
export function toMajorUnits(minor: MinorUnits | bigint, currency: string): string {
  const exponent = currencyExponent(currency);
  const digits = minorUnitsToBigInt(minor)
    .toString()
    .padStart(exponent + 1, '0');
  if (exponent === 0) return digits;
  return `${digits.slice(0, -exponent)}.${digits.slice(-exponent)}`;
}

export function formatMoney(minor: MinorUnits | bigint, currency: string): string {
  return `${toMajorUnits(minor, currency)} ${currency}`;
}

/**
 * Multiply a money amount by a game multiplier and land back on an integer.
 * Slot payouts are `betPerLine * paytableMultiplier`, and the rounding rule has
 * to be identical everywhere or the ledger and the RTP simulation drift apart.
 */
export function multiplyMoney(minor: MinorUnits | bigint, multiplier: number): MinorUnits {
  if (!Number.isFinite(multiplier) || multiplier < 0) {
    throw new Error('money multiplier must be a non-negative finite number');
  }
  const text = String(multiplier);
  const match = /^(\d+)(?:\.(\d+))?$/.exec(text);
  if (!match) throw new Error('money multiplier must use decimal notation');
  const fraction = match[2] ?? '';
  const denominator = 10n ** BigInt(fraction.length);
  const numerator = BigInt(`${match[1]}${fraction}`);
  const product = minorUnitsToBigInt(minor) * numerator;
  return ((product + denominator / 2n) / denominator).toString();
}

/** Basis points: 100 = 1%. Integer representation avoids binary-float money math. */
export const HouseEdgeBps = z.number().int().min(0).max(9_999);
export type HouseEdgeBps = z.infer<typeof HouseEdgeBps>;

/**
 * Percentage RTP for an applied house edge: 9 bps -> 99.91, 0 bps -> 100.
 *
 * Wallet callbacks carry the edge twice, as bps and as a percentage, because
 * operators key their own accounting off one or the other. Deriving the
 * percentage here, in one place, is what stops the two from disagreeing.
 */
export function rtpPctFromEdgeBps(bps: number): number {
  return (10_000 - HouseEdgeBps.parse(bps)) / 100;
}

/**
 * Player-facing zero-edge quota snapshot, included in session/play/state.
 *
 * Zero-edge play is an operator promotion rather than anything a game knows
 * about: the platform runs the round at an edge of zero and counts what it
 * gave away against a daily allowance.
 */
export const ZeroEdgeStatusView = z.object({
  enabled: z.boolean(),
  maxBet: MinorUnits,
  dailyLimit: MinorUnits,
  usedToday: MinorUnits,
  remainingToday: MinorUnits,
  /** ISO-8601 timestamp of the next UTC midnight when the quota resets. */
  resetsAt: z.string(),
});
export type ZeroEdgeStatusView = z.infer<typeof ZeroEdgeStatusView>;
